/**
 * @module features/devices/components/DeviceTable
 * @description Tablet (condensed) and desktop (full grid) device table. Rows are
 * keyboard-activatable and share a fixed 52px height with the skeleton (CLS = 0).
 * Features direct inspect (View) and edit actions.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Eye, Pencil } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DeviceItem } from "../types";
import { DeviceStatusBadge } from "./DeviceStatusBadge";
import { DEVICE_ROW_HEIGHT } from "./DeviceTableSkeleton";
import { deviceTypeLabel, formatDateTime } from "../utils/format";

interface DeviceTableProps {
  devices: DeviceItem[];
  onSelect: (device: DeviceItem) => void;
  onEdit?: (device: DeviceItem) => void;
}

export function DeviceTable({ devices, onSelect, onEdit }: DeviceTableProps) {
  const navigate = useNavigate();
  return (
    <div className="hidden sm:block rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30 border-b border-border/80 h-10">
            <TableHead className="text-xs font-medium">Device</TableHead>
            <TableHead className="hidden md:table-cell text-xs font-medium">MAC address</TableHead>
            <TableHead className="hidden lg:table-cell text-xs font-medium">Firmware / OS</TableHead>
            <TableHead className="text-xs font-medium">Status</TableHead>
            <TableHead className="hidden lg:table-cell text-xs font-medium">Registered</TableHead>
            <TableHead className="text-right text-xs font-medium">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => (
            <TableRow
              key={device.id}
              tabIndex={0}
              onClick={() => onSelect(device)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(device);
                }
              }}
              className={`${DEVICE_ROW_HEIGHT} hover:bg-muted/20 cursor-pointer transition-colors border-b border-border/60 focus-visible:outline-none focus-visible:bg-muted/30`}
            >
              <TableCell className="max-w-[260px]">
                <p className="font-medium text-sm text-foreground truncate">{device.device_name}</p>
                <p className="text-xs text-muted-foreground font-mono truncate">
                  {device.registered_device_id}
                  <span className="font-sans"> · {deviceTypeLabel(device.device_type)}</span>
                </p>
              </TableCell>
              <TableCell className="hidden md:table-cell text-xs font-mono text-muted-foreground">
                {device.mac_address || "—"}
              </TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                {device.device_type === "display_module" && device.display_module?.os_version ? (
                  <span
                    className="inline-flex items-center font-mono text-[11px] px-2 py-0.5 rounded-md bg-muted/60 dark:bg-zinc-800 text-foreground font-medium"
                    title={
                      device.display_module.os_name
                        ? `${device.display_module.os_name} v${device.display_module.os_version}`
                        : `v${device.display_module.os_version}`
                    }
                  >
                    v{device.display_module.os_version}
                  </span>
                ) : (
                  <span
                    className="text-muted-foreground/40 font-mono select-none"
                    title={
                      device.device_type !== "display_module"
                        ? "OS version only applies to display modules"
                        : "No OS version recorded"
                    }
                  >
                    —
                  </span>
                )}
              </TableCell>
              <TableCell>
                <DeviceStatusBadge status={device.status} />
              </TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                {formatDateTime(device.created_at)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/s-admin/devices/${device.id}/telemetry`)}
                    aria-label={`View telemetry for ${device.device_name}`}
                    title="View live telemetry & streaming logs"
                    className="h-8 w-8 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                  >
                    <Activity className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onSelect(device)}
                    aria-label={`Inspect ${device.device_name}`}
                    title="View device details"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {onEdit && device.status !== "DECOMMISSIONED" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(device)}
                      aria-label={`Edit ${device.device_name}`}
                      title="Edit device"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
